import { Link } from "@tanstack/react-router";
import { DashboardLayout } from "@/projects/components/dashboard-layout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/projects/components/ui/table";
import { StatusBadge } from "@/projects/components/status-badge";
import {
  DataPagination,
  useServerPagination,
} from "@/projects/components/data-pagination";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/projects/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/projects/components/ui/popover";
import { Calendar } from "@/projects/components/ui/calendar";
import { Eye, Search, CalendarIcon, X, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { cn } from "@/projects/lib/utils";
import type { Submission, SubmissionStatus } from "@/projects/lib/store";
import {
  fetchStudentVideoSubmissionsPage,
  getSubmissionApiErrorMessage,
  STATUS_API_VALUE,
  toApiDate,
  type StudentVideoSubmissionFilters,
} from "@/projects/lib/submissionsApi";
import { toast } from "@/projects/lib/toast";

function dateToIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildTeacherFilters(input: {
  q: string;
  status: SubmissionStatus | "All";
  fromDate?: Date;
  toDate?: Date;
  quick: "all" | "today" | "7d" | "30d";
}): StudentVideoSubmissionFilters {
  const filters: StudentVideoSubmissionFilters = {};
  let from = input.fromDate;
  let to = input.toDate;

  if (input.quick !== "all") {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    if (input.quick === "today") {
      from = start;
    } else if (input.quick === "7d") {
      const x = new Date(start);
      x.setDate(x.getDate() - 6);
      from = x;
    } else if (input.quick === "30d") {
      const x = new Date(start);
      x.setDate(x.getDate() - 29);
      from = x;
    }
    to = new Date();
  }

  if (from) {
    filters.from_date = toApiDate(dateToIso(from));
  }
  if (to) {
    filters.to_date = toApiDate(dateToIso(to));
  }
  if (input.status !== "All") {
    filters.status = STATUS_API_VALUE[input.status];
  }
  if (input.q.trim()) {
    filters.search = input.q.trim();
  }
  return filters;
}

export function TeacherSubmissions() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [status, setStatus] = useState<SubmissionStatus | "All">("All");
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [quick, setQuick] = useState<"all" | "today" | "7d" | "30d">("all");

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 400);
    return () => window.clearTimeout(t);
  }, [q]);

  const pagination = useServerPagination(
    ({ page, pageSize }) =>
      // Token scopes the list to this teacher's assignments on the API.
      fetchStudentVideoSubmissionsPage(
        buildTeacherFilters({ q: debouncedQ, status, fromDate, toDate, quick }),
        page,
        pageSize
      ),
    [debouncedQ, status, fromDate, toDate, quick],
    {
      onError: error =>
        toast.error(
          getSubmissionApiErrorMessage(
            error,
            "Failed to load assigned submissions."
          )
        ),
    }
  );
  const submissions = pagination.items;
  const loading = pagination.loading;

  const hasFilters =
    status !== "All" || q || fromDate || toDate || quick !== "all";
  const clearAll = () => {
    setStatus("All");
    setQ("");
    setDebouncedQ("");
    setFromDate(undefined);
    setToDate(undefined);
    setQuick("all");
  };

  return (
    <DashboardLayout
      role="teacher"
      title="My Assigned Submissions"
      breadcrumb={["Home", "Assignments"]}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search…"
            className="w-64 pl-8"
          />
        </div>
        <Select
          value={status}
          onValueChange={v => setStatus(v as SubmissionStatus | "All")}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {(
              [
                "All",
                "Assigned for Verification",
                "Not Proper",
                "Ready for Reward",
              ] as const
            ).map(s => (
              <SelectItem key={s} value={s}>
                {s === "Assigned for Verification" ? "Assigned" : s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={quick}
          onValueChange={v => {
            setQuick(v as typeof quick);
            if (v !== "all") {
              setFromDate(undefined);
              setToDate(undefined);
            }
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !fromDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {fromDate ? format(fromDate, "PP") : <span>From date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={fromDate}
              onSelect={d => {
                setFromDate(d);
                setQuick("all");
              }}
              initialFocus
              className={cn("pointer-events-auto p-3")}
            />
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !toDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {toDate ? format(toDate, "PP") : <span>To date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={toDate}
              onSelect={d => {
                setToDate(d);
                setQuick("all");
              }}
              initialFocus
              className={cn("pointer-events-auto p-3")}
            />
          </PopoverContent>
        </Popover>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearAll}>
            <X className="mr-1 h-3.5 w-3.5" /> Clear
          </Button>
        )}
        <div className="text-muted-foreground ml-auto text-xs">
          {loading
            ? "Loading…"
            : `${pagination.meta.count} result${pagination.meta.count === 1 ? "" : "s"}`}
        </div>
      </div>

      <div className="bg-card shadow-soft overflow-hidden rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Reference</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Video Title</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="py-16 text-center">
                    <div className="text-muted-foreground inline-flex items-center gap-2 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading submissions…
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading && submissions.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-muted-foreground py-16 text-center text-sm"
                  >
                    No submissions are currently assigned to you.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                submissions.map(s => {
                  const verifyId = s.apiId || s.id;
                  return (
                    <TableRow key={verifyId} className="hover:bg-muted/40">
                      <TableCell className="text-royal font-mono text-xs">
                        {s.id}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{s.studentName}</div>
                        <div className="text-muted-foreground text-xs">
                          {s.studentId}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[240px]">
                        <div className="truncate font-medium">
                          {s.videoTitle}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {s.fileSizeMB} MB
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {new Date(s.submittedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={s.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Link
                          to="/teacher/verify/$id"
                          params={{ id: verifyId }}
                        >
                          <Button size="sm" variant="outline">
                            <Eye className="mr-1 h-3.5 w-3.5" /> Verify
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
        {!loading && submissions.length > 0 && (
          <DataPagination pagination={pagination} itemLabel="submissions" />
        )}
      </div>
    </DashboardLayout>
  );
}
